/* PTGoalWorkout — new PT tab where Sofia writes free-text goal and gets a
 * tailored gym program including machines. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dumbbell, Target, Loader2, Sparkles, Wrench, Flame, TrendingUp, RefreshCw, ChevronRight, PlayCircle,
} from 'lucide-react';
import ExerciseVideoModal from './ExerciseVideoModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  'framhäva armar och axlar',
  'runda upp rumpan',
  'bränna magfett',
  'tona hela kroppen',
  'starkare rygg och hållning',
  'inre lår + core',
  'bygga större bröst',
  'kondition + smalare midja',
];

const EQUIPMENT_OPTIONS = [
  { id: 'gym',     emoji: '🏋️', label: 'Gym med maskiner', sub: 'Kabel, bänk, leg press…' },
  { id: 'hantlar', emoji: '💪', label: 'Hantlar hemma',    sub: 'Fria vikter + band' },
  { id: 'ingen',   emoji: '🏠', label: 'Bara kroppsvikt',   sub: 'Hemma utan utrustning' },
];

const LEVEL_OPTIONS = ['nybörjare', 'medel', 'avancerad'];

function ExerciseCard({ ex, index, onPlayVideo }) {
  return (
    <div className="rounded-xl border-2 border-pink-100 bg-white p-4 hover:border-fuchsia-300 hover:shadow-md transition group">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-400 to-fuchsia-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
          {index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="font-bold text-slate-900 flex-1">{ex.name}</div>
            <button
              type="button"
              onClick={() => onPlayVideo?.(ex)}
              data-testid={`exercise-play-video-${index}`}
              title="Se demo-video"
              className="text-fuchsia-500 hover:text-fuchsia-700 opacity-70 group-hover:opacity-100 flex items-center gap-0.5 text-[11px] flex-shrink-0"
            >
              <PlayCircle className="w-5 h-5" />
              <span className="hidden sm:inline">Demo</span>
            </button>
          </div>
          {ex.machine && (
            <div className="text-[11px] text-fuchsia-700 bg-fuchsia-50 border border-fuchsia-200 rounded-full px-2 py-0.5 mt-1 inline-flex items-center gap-1">
              <Wrench className="w-3 h-3" /> {ex.machine}
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-2 text-[11px]">
            {ex.sets && (
              <span className="bg-pink-50 border border-pink-200 rounded-full px-2 py-0.5 text-pink-800">
                {ex.sets} × {ex.reps}
              </span>
            )}
            {ex.rest_sec && (
              <span className="bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5 text-slate-600">
                Vila {ex.rest_sec}s
              </span>
            )}
            {ex.muscle && (
              <span className="bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 text-amber-800">
                🎯 {ex.muscle}
              </span>
            )}
          </div>
          {ex.how_to && <p className="text-sm text-slate-700 mt-2 leading-relaxed">{ex.how_to}</p>}
          {ex.tip && (
            <p className="text-[11px] text-fuchsia-700 bg-fuchsia-50 border border-fuchsia-100 rounded-lg px-2 py-1 mt-2">
              💡 <b>Tips:</b> {ex.tip}
            </p>
          )}
          {(ex.cues || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {ex.cues.map((c, i) => (
                <span key={i} className="text-[10px] italic text-slate-500">“{c}”</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PTGoalWorkout() {
  const [focusText, setFocusText] = useState('');
  const [duration, setDuration] = useState(45);
  const [equipment, setEquipment] = useState('gym');
  const [level, setLevel] = useState('medel');
  const [days, setDays] = useState(3);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [videoExercise, setVideoExercise] = useState(null);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/pt/goal-workouts`, { withCredentials: true });
      setHistory(data.items || []);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const generate = async () => {
    const t = focusText.trim();
    if (t.length < 3) { toast.error('Skriv vad du vill fokusera på 💪'); return; }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/pt/goal-workout`,
        { focus_text: t, duration_min: duration, equipment, level, days_per_week: days },
        { withCredentials: true, timeout: 120000 },
      );
      setResult(data);
      toast.success('Ditt program är klart 💪✨');
      loadHistory();
      // scroll to top of result
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunde inte bygga programmet');
    } finally { setBusy(false); }
  };

  const loadFromHistory = (item) => {
    setFocusText(item.focus_text || '');
    setEquipment(item.equipment || 'gym');
    setLevel(item.level || 'medel');
    setDuration(item.duration_min || 45);
    setDays(item.days_per_week || 3);
    setResult(item);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="pt-goal-workout-page">
      {/* Hero */}
      <div className="rounded-3xl p-5 bg-gradient-to-br from-pink-100 via-fuchsia-100 to-violet-100 border border-fuchsia-200 relative overflow-hidden">
        <div className="absolute -right-4 -top-4 opacity-20 text-8xl leading-none pointer-events-none">🎯</div>
        <div className="flex items-center gap-2 mb-1">
          <Target className="w-5 h-5 text-fuchsia-700" />
          <div className="text-[11px] uppercase font-semibold tracking-wider text-fuchsia-800/80">
            PT · Målpass
          </div>
        </div>
        <h1 className="text-2xl font-bold text-fuchsia-900" style={{ fontFamily: 'ui-serif, Georgia, serif' }}>
          Skriv ditt mål — jag bygger programmet 💪
        </h1>
        <p className="text-sm text-fuchsia-800/70 mt-1">
          Beskriv med dina ord vad du vill uppnå. Nova väljer rätt övningar, maskiner, seter och reps.
        </p>
      </div>

      {/* Form */}
      {!result && (
        <div className="rounded-2xl p-5 bg-white border border-pink-100 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-800 mb-1">Vad vill du fokusera på?</label>
            <textarea
              value={focusText}
              onChange={(e) => setFocusText(e.target.value)}
              rows={3}
              maxLength={400}
              placeholder='T.ex. "framhäva armar", "bränna magfett", "runda upp rumpan"…'
              data-testid="pt-goal-focus-text"
              className="w-full rounded-xl border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-200"
            />
            <div className="flex flex-wrap gap-1 mt-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setFocusText(s)}
                  className="text-[11px] px-2 py-1 rounded-full bg-fuchsia-50 border border-fuchsia-200 text-fuchsia-800 hover:bg-fuchsia-100"
                >
                  + {s}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-800 mb-2">Utrustning</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {EQUIPMENT_OPTIONS.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => setEquipment(e.id)}
                  data-testid={`pt-goal-equip-${e.id}`}
                  className={`text-left rounded-xl p-3 border transition ${
                    equipment === e.id
                      ? 'bg-fuchsia-50 border-fuchsia-400 ring-2 ring-fuchsia-200'
                      : 'bg-white border-slate-200 hover:border-fuchsia-300'
                  }`}
                >
                  <div className="text-2xl leading-none">{e.emoji}</div>
                  <div className="text-sm font-medium text-slate-800 mt-1">{e.label}</div>
                  <div className="text-[11px] text-slate-500">{e.sub}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-semibold text-slate-800 mb-1">Passets längd</label>
              <div className="flex gap-1 flex-wrap">
                {[20, 30, 45, 60, 90].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setDuration(n)}
                    className={`px-2.5 py-1 rounded-full text-xs border ${
                      duration === n
                        ? 'bg-fuchsia-500 text-white border-fuchsia-500'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-fuchsia-300'
                    }`}
                  >
                    {n} min
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-800 mb-1">Nivå</label>
              <div className="flex gap-1 flex-wrap">
                {LEVEL_OPTIONS.map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => setLevel(l)}
                    className={`px-2.5 py-1 rounded-full text-xs border ${
                      level === l
                        ? 'bg-fuchsia-500 text-white border-fuchsia-500'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-fuchsia-300'
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-800 mb-1">Pass per vecka</label>
              <div className="flex gap-1 flex-wrap">
                {[2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setDays(n)}
                    className={`px-2.5 py-1 rounded-full text-xs border ${
                      days === n
                        ? 'bg-fuchsia-500 text-white border-fuchsia-500'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-fuchsia-300'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={generate}
            disabled={busy || focusText.trim().length < 3}
            data-testid="pt-goal-generate"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-pink-500 via-fuchsia-500 to-violet-500 text-white font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Nova bygger ditt program (30-60s)…</>
            ) : (
              <><Sparkles className="w-4 h-4" /> Bygg mitt program</>
            )}
          </button>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="space-y-4">
          <div className="rounded-2xl p-5 bg-gradient-to-br from-pink-50 via-fuchsia-50 to-violet-50 border-2 border-fuchsia-200">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div>
                <div className="text-[11px] uppercase font-bold text-fuchsia-700 tracking-wider">Målpass</div>
                <h2 className="text-2xl font-bold text-fuchsia-900" style={{ fontFamily: 'ui-serif, Georgia, serif' }}>
                  {result.name}
                </h2>
                {result.focus_text && (
                  <div className="text-xs text-fuchsia-700 mt-1 italic">för “{result.focus_text}”</div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setResult(null)}
                data-testid="pt-goal-reset"
                className="text-xs text-fuchsia-700 hover:text-fuchsia-900 flex items-center gap-1 whitespace-nowrap"
              >
                <RefreshCw className="w-3 h-3" /> Nytt mål
              </button>
            </div>
            {result.intro && <p className="text-sm text-slate-700 mt-2">{result.intro}</p>}

            {(result.target_areas || []).length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] uppercase font-bold text-fuchsia-700 tracking-wider mb-1">🎯 Vi jobbar på</div>
                <div className="flex flex-wrap gap-1.5">
                  {result.target_areas.map((a, i) => (
                    <span key={i} className="text-xs bg-white border border-fuchsia-300 rounded-full px-2 py-0.5 text-fuchsia-800">
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {result.expected_outcome && (
              <div className="mt-3 rounded-xl bg-white/80 border border-fuchsia-200 p-3">
                <div className="text-[10px] uppercase font-bold text-fuchsia-700 tracking-wider mb-1 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> Förväntat resultat
                </div>
                <p className="text-sm text-slate-700">{result.expected_outcome}</p>
                {result.session_frequency && (
                  <p className="text-[11px] text-fuchsia-700 mt-1 font-medium">📅 {result.session_frequency}</p>
                )}
              </div>
            )}

            {result.warmup && (
              <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3">
                <div className="text-[10px] uppercase font-bold text-amber-700 tracking-wider mb-1 flex items-center gap-1">
                  <Flame className="w-3 h-3" /> Uppvärmning
                </div>
                <p className="text-sm text-amber-900">{result.warmup}</p>
              </div>
            )}
          </div>

          {/* Exercises */}
          <div>
            <div className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5">
              <Dumbbell className="w-4 h-4 text-fuchsia-600" /> Övningar ({(result.exercises || []).length})
            </div>
            <div className="space-y-2">
              {(result.exercises || []).map((ex, i) => (
                <ExerciseCard key={i} ex={ex} index={i} onPlayVideo={setVideoExercise} />
              ))}
            </div>
          </div>

          {result.progression_tip && (
            <div className="rounded-xl bg-violet-50 border border-violet-200 p-3">
              <div className="text-[10px] uppercase font-bold text-violet-700 tracking-wider mb-1 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> Progression
              </div>
              <p className="text-sm text-violet-900">{result.progression_tip}</p>
            </div>
          )}

          {result.outro && (
            <p className="text-sm italic text-fuchsia-800 text-center border-t border-fuchsia-100 pt-3">
              💜 {result.outro}
            </p>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="rounded-2xl bg-white border border-pink-100 p-4">
          <div className="text-sm font-semibold text-slate-800 mb-2">🗂 Tidigare program ({history.length})</div>
          <div className="space-y-1.5">
            {history.map((h) => (
              <button
                key={h.id}
                type="button"
                onClick={() => loadFromHistory(h)}
                data-testid={`pt-goal-history-${h.id}`}
                className="w-full text-left rounded-lg border border-slate-200 hover:border-fuchsia-300 hover:bg-fuchsia-50/50 p-2.5 transition flex items-center gap-2"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-slate-800 truncate">{h.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {h.focus_text} · {h.equipment} · {h.duration_min} min
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
            ))}
          </div>
        </div>
      )}

      {videoExercise && (
        <ExerciseVideoModal exercise={videoExercise} onClose={() => setVideoExercise(null)} />
      )}
    </div>
  );
}
